import React, { useState, useRef, useEffect } from "react";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import moment from "moment";

const PURPLE       = "#7c4dff";
const PURPLE_LIGHT = "#ede7ff";
const COLORS = [
    "#7c4dff", "#26c6da", "#ff7043", "#66bb6a",
    "#ffa726", "#ec407a", "#42a5f5", "#ab47bc",
    "#26a69a", "#d4e157",
];

// ─── Responsive container width hook ─────────────────────────────────────────
const useContainerWidth = (fallback = 500) => {
    const ref  = useRef(null);
    const [width, setWidth] = useState(fallback);

    useEffect(() => {
        if (!ref.current) return;
        const obs = new ResizeObserver(entries => {
            const w = entries[0]?.contentRect?.width;
            if (w && w > 0) setWidth(Math.floor(w));
        });
        obs.observe(ref.current);
        // Initial read
        setWidth(Math.floor(ref.current.offsetWidth) || fallback);
        return () => obs.disconnect();
    }, []);

    return [ref, width];
};

// ─── Badge pill ───────────────────────────────────────────────────────────────
const Badge = ({ children }) => (
    <Box sx={{
        bgcolor: PURPLE_LIGHT, color: PURPLE, fontWeight: 700,
        fontSize: 11, px: 1.2, py: 0.3, borderRadius: "6px",
        whiteSpace: "nowrap", flexShrink: 0,
    }}>
        {children}
    </Box>
);

// ─── 1. Activity Trend Line Chart ─────────────────────────────────────────────
const TrendChart = ({ data }) => {
    const [containerRef, width] = useContainerWidth(500);
    const svgRef  = useRef(null);
    const [tooltip, setTooltip] = useState({ visible: false });

    if (!data || data.length === 0) return (
        <Box ref={containerRef} sx={{ width: "100%", textAlign: "center", py: 5, color: "#bbb", fontSize: 13 }}>
            No trend data available
        </Box>
    );

    // Dimensions — padR large enough so last dot value label never clips
    const h    = 230;
    const padL = 44;   // room for Y-axis numbers
    const padR = 28;   // right breathing room
    const padT = 28;   // top so value labels above dots don't clip
    const padB = 38;   // bottom for X date labels

    const cw = Math.max(width - padL - padR, 1);
    const ch = h - padT - padB;
    const max = Math.max(...data.map(d => d.value), 1);

    const getX = i  => padL + (i / Math.max(data.length - 1, 1)) * cw;
    const getY = v  => padT + ch - (v / max) * ch;

    const linePath = data.map((d, i) => {
        if (i === 0) return `M ${getX(0)} ${getY(d.value)}`;
        const x0 = getX(i - 1), y0 = getY(data[i - 1].value);
        const x1 = getX(i),     y1 = getY(d.value);
        return `C ${(x0 + x1) / 2} ${y0}, ${(x0 + x1) / 2} ${y1}, ${x1} ${y1}`;
    }).join(" ");

    const areaPath = `${linePath} L ${getX(data.length - 1)} ${padT + ch} L ${getX(0)} ${padT + ch} Z`;
    const yTicks   = [0, 0.25, 0.5, 0.75, 1];

    return (
        <Box ref={containerRef} sx={{ width: "100%", overflow: "hidden" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1, gap: 1 }}>
                <Typography fontWeight={700} fontSize={14} color="text.primary" noWrap>Activity Trend</Typography>
                <Badge>Last 7 days</Badge>
            </Box>

            {/* viewBox makes SVG scale on very small screens */}
            <svg
                ref={svgRef}
                width="100%"
                viewBox={`0 0 ${width} ${h}`}
                style={{ display: "block", overflow: "visible" }}
            >
                <defs>
                    <linearGradient id="trendAreaGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%"   stopColor={PURPLE} stopOpacity="0.16" />
                        <stop offset="100%" stopColor={PURPLE} stopOpacity="0.01" />
                    </linearGradient>
                </defs>

                {/* Y grid + labels */}
                {yTicks.map((t, i) => {
                    const y = padT + ch * (1 - t);
                    return (
                        <g key={i}>
                            <line x1={padL} y1={y} x2={width - padR} y2={y} stroke="#f0f0f0" strokeWidth={1} />
                            <text x={padL - 7} y={y + 4} fontSize={10} fill="#bbb" textAnchor="end" fontFamily="inherit">
                                {Math.round(t * max)}
                            </text>
                        </g>
                    );
                })}

                {/* Area + Line */}
                <path d={areaPath} fill="url(#trendAreaGrad)" />
                <path d={linePath} fill="none" stroke={PURPLE} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />

                {/* Dots + X labels + value labels */}
                {data.map((d, i) => {
                    const cx   = getX(i);
                    const cy   = getY(d.value);
                    const dateLabel = moment(d.label).format("MMM D");
                    // Show all labels when ≤7 points, else thin out
                    const showDate  = data.length <= 7 || i % Math.ceil(data.length / 7) === 0 || i === data.length - 1;

                    return (
                        <g key={i}>
                            {showDate && (
                                <text x={cx} y={h - 8} fontSize={10} fill="#aaa" textAnchor="middle" fontFamily="inherit">
                                    {dateLabel}
                                </text>
                            )}
                            {/* Value above dot */}
                            <text x={cx} y={cy - 11} fontSize={10} fill={PURPLE} fontWeight={700} textAnchor="middle" pointerEvents="none" fontFamily="inherit">
                                {d.value}
                            </text>
                            {/* Interactive dot */}
                            <circle
                                cx={cx} cy={cy} r={5}
                                fill={PURPLE} stroke="#fff" strokeWidth={2}
                                style={{ cursor: "pointer" }}
                                onMouseEnter={() => {
                                    const rect = svgRef.current?.getBoundingClientRect();
                                    if (!rect) return;
                                    // Scale from viewBox coords to screen coords
                                    const scaleX = rect.width  / width;
                                    const scaleY = rect.height / h;
                                    setTooltip({
                                        visible: true,
                                        x: rect.left + cx * scaleX,
                                        y: rect.top  + cy * scaleY,
                                        label: moment(d.label).format("ddd, MMM D"),
                                        value: d.value,
                                    });
                                }}
                                onMouseLeave={() => setTooltip({ visible: false })}
                            />
                        </g>
                    );
                })}
            </svg>

            {tooltip.visible && (
                <Box sx={{
                    position: "fixed",
                    top:  tooltip.y - 52,
                    left: tooltip.x + 12,
                    bgcolor: "#fff", color: PURPLE,
                    boxShadow: "0 4px 16px rgba(124,77,255,0.18)",
                    borderRadius: "8px", px: 1.5, py: 0.8,
                    fontSize: 12, fontWeight: 600,
                    pointerEvents: "none", zIndex: 1500,
                    border: `1px solid ${PURPLE_LIGHT}`,
                    minWidth: 110,
                }}>
                    <div style={{ color: "#999", fontWeight: 400, fontSize: 11 }}>{tooltip.label}</div>
                    <div>{tooltip.value} activities</div>
                </Box>
            )}
        </Box>
    );
};

// ─── 2. Top 5 Active Users Bar Chart ─────────────────────────────────────────
const TopUsersChart = ({ data }) => {
    const [containerRef, width] = useContainerWidth(300);
    const [hovered, setHovered] = useState(null);

    if (!data || data.length === 0) return (
        <Box ref={containerRef} sx={{ width: "100%", textAlign: "center", py: 5, color: "#bbb", fontSize: 13 }}>
            No user data available
        </Box>
    );

    const top = data.slice(0, 5);
    const max = top[0]?.count || 1;
    // Compute max digit width for count label so padR is always sufficient
    const maxCountStr  = String(max);
    const countLabelW  = maxCountStr.length * 8 + 10; // ~8px per char + breathing room

    const BADGE_W  = 22;  // rank badge
    const NAME_W   = 90;  // name text area (truncated)
    const GAP      = 8;   // gap between name and bar
    const padL     = BADGE_W + GAP + NAME_W + GAP; // total left of bar
    const padR     = countLabelW;                   // right of bar = count label width
    const padT     = 6;
    const padB     = 6;
    const rowH     = 42;
    const barH     = 22;
    const chartW   = Math.max(width - padL - padR, 20);
    const svgH     = padT + top.length * rowH + padB;

    return (
        <Box ref={containerRef} sx={{ width: "100%", overflow: "hidden" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1, gap: 1 }}>
                <Typography fontWeight={700} fontSize={14} color="text.primary" noWrap>Most Active Users</Typography>
                <Badge>Top 5</Badge>
            </Box>

            <svg
                width="100%"
                viewBox={`0 0 ${width} ${svgH}`}
                style={{ display: "block", overflow: "visible" }}
            >
                {top.map((d, i) => {
                    const barW  = Math.max(4, (d.count / max) * chartW);
                    const y     = padT + i * rowH + (rowH - barH) / 2;
                    const isTop = i === 0;
                    const isHov = hovered === i;

                    // Truncate name to fit NAME_W (~11 chars at 8px each)
                    const maxChars = Math.floor(NAME_W / 7.5);
                    const nameStr  = d.name.length > maxChars ? d.name.slice(0, maxChars) + "…" : d.name;

                    return (
                        <g key={i} style={{ cursor: "pointer" }}
                           onMouseEnter={() => setHovered(i)}
                           onMouseLeave={() => setHovered(null)}>

                            {/* Rank badge */}
                            <rect x={0} y={y + 2} width={BADGE_W} height={18} rx={5}
                                  fill={isTop ? PURPLE : isHov ? PURPLE : "#f0eeff"} />
                            <text x={BADGE_W / 2} y={y + 14} fontSize={10}
                                  fill={isTop || isHov ? "#fff" : PURPLE}
                                  textAnchor="middle" fontWeight={800} fontFamily="inherit">
                                {i + 1}
                            </text>

                            {/* Name */}
                            <text x={BADGE_W + GAP} y={y + barH / 2 + 4} fontSize={11.5}
                                  fill={isHov || isTop ? PURPLE : "#444"}
                                  fontWeight={isHov || isTop ? 700 : 400}
                                  fontFamily="inherit">
                                {nameStr}
                            </text>

                            {/* Bar track */}
                            <rect x={padL} y={y} width={chartW} height={barH} fill="#f5f3ff" rx={6} />

                            {/* Bar fill */}
                            <rect x={padL} y={y} width={barW} height={barH}
                                  fill={isTop ? PURPLE : isHov ? PURPLE : PURPLE_LIGHT}
                                  rx={6}
                                  style={{ transition: "width 0.3s ease, fill 0.2s ease" }} />

                            {/* Count — always outside bar to the right, never overlaps */}
                            <text
                                x={padL + barW + 6}
                                y={y + barH / 2 + 4}
                                fontSize={11} fontWeight={700}
                                fill={isTop || isHov ? PURPLE : "#aaa"}
                                fontFamily="inherit">
                                {d.count}
                            </text>
                        </g>
                    );
                })}
            </svg>
        </Box>
    );
};

// ─── 3. Top 10 Activity Categories Donut ─────────────────────────────────────
const DonutChart = ({ data }) => {
    const theme   = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [hovered, setHovered] = useState(null);

    if (!data || data.length === 0) return (
        <Box sx={{ width: "100%", textAlign: "center", py: 5, color: "#bbb", fontSize: 13 }}>
            No category data available
        </Box>
    );

    const top   = data.slice(0, 10);
    const total = top.reduce((s, d) => s + d.count, 0);

    // Donut dimensions — smaller on mobile
    const donutSize = isMobile ? 150 : 190;
    const cx = donutSize / 2;
    const cy = donutSize / 2;
    const r  = donutSize * 0.36;
    const ir = donutSize * 0.22;

    let angle = -Math.PI / 2;
    const slices = top.map((d, i) => {
        const sweep    = (d.count / total) * 2 * Math.PI;
        const midAngle = angle + sweep / 2;
        const x1 = cx + r * Math.cos(angle);
        const y1 = cy + r * Math.sin(angle);
        angle += sweep;
        const x2  = cx + r * Math.cos(angle);
        const y2  = cy + r * Math.sin(angle);
        const xi1 = cx + ir * Math.cos(angle - sweep);
        const yi1 = cy + ir * Math.sin(angle - sweep);
        const xi2 = cx + ir * Math.cos(angle);
        const yi2 = cy + ir * Math.sin(angle);
        const large = sweep > Math.PI ? 1 : 0;
        return {
            path: `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} L ${xi2} ${yi2} A ${ir} ${ir} 0 ${large} 0 ${xi1} ${yi1} Z`,
            color: COLORS[i % COLORS.length],
            midAngle,
            ...d,
        };
    });

    const hov = hovered !== null ? slices[hovered] : null;

    const LegendItem = ({ s, i }) => (
        <Box
            sx={{
                display: "flex", alignItems: "center", gap: 0.8, py: 0.3,
                cursor: "pointer",
                opacity: hovered === null || hovered === i ? 1 : 0.4,
                transition: "opacity 0.18s",
            }}
            onMouseEnter={() => setHovered(i)}
            onMouseLeave={() => setHovered(null)}
        >
            <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: s.color, flexShrink: 0 }} />
            <Typography
                fontSize={11} noWrap
                sx={{ flex: 1, color: hovered === i ? s.color : "black", fontWeight: hovered === i ? 700 : 400 }}
            >
                {s.label}
            </Typography>
            <Typography fontSize={11} fontWeight={700} sx={{ color: hovered === i ? s.color : "text.primary", ml: 1, flexShrink: 0 }}>
                {((s.count / total) * 100).toFixed(0)}%
            </Typography>
        </Box>
    );

    return (
        <Box sx={{ width: "100%" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5, gap: 1 }}>
                <Typography fontWeight={700} fontSize={14} color="text.primary" noWrap>Activity Categories</Typography>
                <Badge>Top 10</Badge>
            </Box>

            <Box sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "center", sm: "flex-start" },
                gap: { xs: 2, sm: 3 },
            }}>
                {/* Donut SVG */}
                <Box sx={{ flexShrink: 0 }}>
                    <svg width={donutSize} height={donutSize} style={{ display: "block", overflow: "visible" }}>
                        {slices.map((s, i) => (
                            <path key={i} d={s.path} fill={s.color}
                                  style={{
                                      cursor: "pointer",
                                      opacity: hovered === null || hovered === i ? 1 : 0.38,
                                      transform: hovered === i
                                          ? `translate(${Math.cos(s.midAngle) * 5}px, ${Math.sin(s.midAngle) * 5}px)`
                                          : "none",
                                      transition: "opacity 0.18s, transform 0.18s",
                                  }}
                                  onMouseEnter={() => setHovered(i)}
                                  onMouseLeave={() => setHovered(null)}
                            />
                        ))}
                        <circle cx={cx} cy={cy} r={ir - 2} fill="#fff" />
                        <text x={cx} y={cy - 9} textAnchor="middle"
                              fontSize={hov ? 16 : 20} fontWeight={800}
                              fill={hov ? hov.color : PURPLE} fontFamily="inherit">
                            {hov ? hov.count : total}
                        </text>
                        <text x={cx} y={cy + 7} textAnchor="middle" fontSize={9} fill="#bbb" fontFamily="inherit">
                            {hov ? "actions" : "total"}
                        </text>
                        {hov && (
                            <text x={cx} y={cy + 21} textAnchor="middle" fontSize={10} fill={hov.color} fontWeight={700} fontFamily="inherit">
                                {((hov.count / total) * 100).toFixed(1)}%
                            </text>
                        )}
                    </svg>
                </Box>

                {/* Legend — single column */}
                <Box sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignContent: "start",
                    width: "100%",
                    minWidth: 0,
                }}>
                    {slices.map((s, i) => (
                        <LegendItem key={i} s={s} i={i} />
                    ))}
                </Box>
            </Box>
        </Box>
    );
};

// ─── Main Export ──────────────────────────────────────────────────────────────
const ActivityCharts = ({ trendData, userData, categoryData }) => {
    const hasTrend    = trendData    && trendData.length    > 0;
    const hasUsers    = userData     && userData.length     > 0;
    const hasCategory = categoryData && categoryData.length > 0;

    if (!hasTrend && !hasUsers && !hasCategory) return null;

    return (
        <Box sx={{ width: "100%", mb: 2 }}>

            {/* Row 1: Trend (flex:2) + Top Users (flex:1) */}
            {(hasTrend || hasUsers) && (
                <Box sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    gap: 2, mb: 2,
                }}>
                    {hasTrend && (
                        <Box sx={{
                            flex: 2, minWidth: 0,   // minWidth:0 prevents flex overflow
                            bgcolor: "#fafafa", borderRadius: "12px",
                            p: { xs: 1.5, sm: 2 },
                            boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                        }}>
                            <TrendChart data={trendData} />
                        </Box>
                    )}
                    {hasUsers && (
                        <Box sx={{
                            flex: 1, minWidth: 0,
                            bgcolor: "#fafafa", borderRadius: "12px",
                            p: { xs: 1.5, sm: 2 },
                            boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                        }}>
                            <TopUsersChart data={userData} />
                        </Box>
                    )}
                </Box>
            )}

            {/* Row 2: Category Donut — full width */}
            {hasCategory && (
                <Box sx={{
                    bgcolor: "#fafafa", borderRadius: "12px",
                    p: { xs: 1.5, sm: 2 },
                    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                }}>
                    <DonutChart data={categoryData} />
                </Box>
            )}
        </Box>
    );
};

export default ActivityCharts;
export { TrendChart, TopUsersChart, DonutChart };